import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { BACKEND_CONFIG } from '../config/backendConfig';

interface Role {
  role_id: number;
  role_name: string;
  role_description: string;
  granted_at: string;
  granted_by: string | null;
  expires_at: string | null;
}

interface PermissionsContextType {
  permissions: string[];
  roles: Role[];
  loading: boolean;
  error: string | null;
  can: (permission: string) => boolean;
  hasRole: (roleName: string) => boolean;
  refreshPermissions: () => Promise<void>;
}

const PermissionsContext = createContext<PermissionsContextType | undefined>(undefined);

export const usePermissions = () => {
  const context = useContext(PermissionsContext);
  if (!context) {
    throw new Error('usePermissions must be used within a PermissionsProvider');
  }
  return context;
};

interface PermissionsProviderProps {
  children: ReactNode;
}

export const PermissionsProvider: React.FC<PermissionsProviderProps> = ({ children }) => {
  const { user, sessionToken } = useAuth();
  const [permissions, setPermissions] = useState<string[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Who the current permissions belong to. Between the user loading and the
  // fetch starting, permissions are still empty. Report that as loading, or
  // route guards read "no permissions" and redirect.
  const [loadedFor, setLoadedFor] = useState<string | null>(null);
  const currentKey = user && sessionToken ? `${user.id}:${sessionToken}` : null;

  const fetchPermissions = async () => {
    if (!user || !sessionToken) {
      setPermissions([]);
      setRoles([]);
      setLoadedFor(null);
      setLoading(false);
      return;
    }
    const key = `${user.id}:${sessionToken}`;

    try {
      setLoading(true);
      setError(null);

      // Fetch permissions and roles from the RBAC API
      const [permissionsRes, rolesRes] = await Promise.all([
        fetch(`${BACKEND_CONFIG.baseURL}/roles/my-permissions`, {
          headers: {
            'Authorization': `Bearer ${sessionToken}`,
          },
        }),
        fetch(`${BACKEND_CONFIG.baseURL}/roles/my-roles`, {
          headers: {
            'Authorization': `Bearer ${sessionToken}`,
          },
        }),
      ]);

      if (!permissionsRes.ok || !rolesRes.ok) {
        throw new Error('Failed to fetch permissions');
      }

      const permissionsData = await permissionsRes.json();
      const rolesData = await rolesRes.json();

      setPermissions(permissionsData.permissions || []);
      setRoles(rolesData.roles || []);
    } catch (err) {
      console.error('Error fetching permissions:', err);
      setError(err instanceof Error ? err.message : 'Failed to load permissions');
      // No fallback - if permissions fail to load, user has no permissions
      setPermissions([]);
      setRoles([]);
    } finally {
      setLoadedFor(key);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPermissions();
  }, [user, sessionToken]);

  /**
   * Check if user has a specific permission
   * Supports wildcard matching:
   * - '*:*' matches everything
   * - 'resource:*' matches all actions on that resource
   * - 'resource:action' matches exact permission
   */
  const can = (permission: string): boolean => {
    if (!permission || permissions.length === 0) {
      return false;
    }

    // Check for exact match first
    if (permissions.includes(permission)) {
      return true;
    }

    // Check for wildcard permissions
    if (permissions.includes('*:*')) {
      return true;
    }

    // Check for resource wildcard (e.g., 'calendar:*' matches 'calendar:create')
    const [resource] = permission.split(':');
    if (permissions.includes(`${resource}:*`)) {
      return true;
    }

    return false;
  };

  /**
   * Check if user has a specific role
   */
  const hasRole = (roleName: string): boolean => {
    return roles.some(role => role.role_name === roleName);
  };

  const value: PermissionsContextType = {
    permissions,
    roles,
    loading: loading || (currentKey !== null && loadedFor !== currentKey),
    error,
    can,
    hasRole,
    refreshPermissions: fetchPermissions,
  };

  return (
    <PermissionsContext.Provider value={value}>
      {children}
    </PermissionsContext.Provider>
  );
};
